import React, { useState } from 'react';
import { ModalProps } from 'types/Modal';
import {
    NButton,
    NDialog,
    NDivider,
    NIcon,
    NSelect,
} from '@innersource-nn/novoui-react';
import useDatasetOpenS3 from 'views/Datasets/hooks/useDatasetOpenS3';
import useEnvironmentCredentials from 'views/Environments/hooks/useEnvironmentCredentials';
import CredentialsContainer from 'components/modal/CredentialsModal/CredentialsContainer';
import { Credential } from 'types/Credentials';
import Spinner from 'components/loader/Spinner';

export interface AWSResourcesModalProps extends ModalProps {
    environmentUri: string;
    title: string;
    datasetUri?: string;
}

export default function AWSResourcesModal({
    environmentUri,
    title,
    datasetUri,
    visible,
    onClose,
}: AWSResourcesModalProps) {
    const { loadingButton, goToS3Console } = useDatasetOpenS3(datasetUri ?? '');
    const {
        loading,
        accountOptions,
        roleOptions,
        urlLoading,
        credentialLoading,
        getAWSUrl,
        getCredentials,
    } = useEnvironmentCredentials(environmentUri);

    const [selectedGroup, setSelectedGroup] = useState('');
    const [environmentType, setEnvironmentType] = useState('');
    const [selectedAccount, setSelectedAccount] = useState('');
    const [selectedCurrentAccount, setSelectedCurrentAccount] = useState('');
    const [credentialsOpen, setCredentialsOpen] = useState(false);
    const [credentials, setCredentials] = useState<Credential[]>([]);
    const [copyCredentials, setCopyCredentials] = useState<string[]>([]);

    /**
     * Handles changes in the <select> element by parsing the selected value.
     * The selected value is expected to be in the format "ENVIRONMENT ACCOUNT_NUMBER"  The function extracts the environment and account number,
     * and updates the corresponding state variables. If the environment is "PROD",
     * it sets the environment state to an empty string.
     */
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const matches = e.target.value.match(/(\D+) (\d+)/);

        if (matches) {
            const environmentType = matches[1];
            const account = matches[2];

            setEnvironmentType(environmentType.toLowerCase());
            setSelectedCurrentAccount(account);
            setSelectedAccount(e.target.value);
        }
    };

    const handleCredentialsClick = async () => {
        const response = await getCredentials(
            environmentType,
            selectedGroup,
            selectedCurrentAccount,
        );
        if (response !== null) {
            setCopyCredentials(response.copyCredentials);
            setCredentials(response.credentials);
            setCredentialsOpen(true);
        }
    };
    return (
        <NDialog
            isVisible={visible}
            closeDialog={onClose}
            backgroundClose
            headline={title}
            primaryLabel="Console"
            dialogHeader={
                credentialsOpen ? (
                    <div className="dialog-header">
                        <div
                            className="cursor-pointer text-true-blue my-8 w-28 gap-8 flex items-center"
                            onClick={() => setCredentialsOpen(false)}
                        >
                            <NIcon
                                iconName="chevronLeft"
                                size={16}
                                color="true-blue"
                                className="fill-true-blue"
                            />
                            Go back
                        </div>
                    </div>
                ) : null
            }
            size={credentialsOpen ? 'large' : 'medium'}
            showDividers={false}
        >
            <div className="text-true-blue">
                <div>
                    {datasetUri && (
                        <div className="mt-16">
                            <b>Link to S3 bucket</b>
                            <NButton
                                leftIcon="external"
                                className="w-full my-16"
                                square
                                variant="outline"
                                isLoading={loadingButton}
                                onClick={goToS3Console}
                            >
                                Go to S3 bucket
                            </NButton>
                            <NDivider orientation="horizontal" />
                        </div>
                    )}
                    <div>
                        {loading ? (
                            <Spinner />
                        ) : (
                            <div>
                                {!credentialsOpen ? (
                                    <div className="mt-16">
                                        <p className="font-light">
                                            {datasetUri
                                                ? 'Generate link for AWS Console'
                                                : 'Generate credentials or AWS console links for your groups'}
                                        </p>
                                        <div className="grid grid-cols-2 gap-8 mt-16">
                                            <NSelect
                                                name="URLgroup"
                                                label="Target group"
                                                modelValue={selectedGroup}
                                                placeholder="Select target group"
                                                onChange={(e) =>
                                                    setSelectedGroup(
                                                        e.target.value,
                                                    )
                                                }
                                                variant="outline"
                                                options={roleOptions}
                                            />
                                            <NSelect
                                                name="URLaccount"
                                                label="Account"
                                                modelValue={selectedAccount}
                                                onChange={handleChange}
                                                options={accountOptions}
                                                variant="outline"
                                                placeholder="Select account"
                                            />
                                        </div>

                                        {!datasetUri ? (
                                            <div className="flex gap-8 my-16">
                                                <NButton
                                                    variant="outline"
                                                    leftIcon="doselog"
                                                    onClick={
                                                        handleCredentialsClick
                                                    }
                                                    isLoading={
                                                        credentialLoading
                                                    }
                                                    disabled={
                                                        selectedAccount ===
                                                            '' ||
                                                        selectedGroup === ''
                                                    }
                                                    square
                                                >
                                                    Credentials
                                                </NButton>
                                                <NButton
                                                    variant="outline"
                                                    leftIcon="external"
                                                    onClick={() =>
                                                        getAWSUrl(
                                                            environmentType,
                                                            selectedGroup,
                                                            selectedCurrentAccount,
                                                        )
                                                    }
                                                    isLoading={urlLoading}
                                                    disabled={
                                                        selectedAccount ===
                                                            '' ||
                                                        selectedGroup === ''
                                                    }
                                                    square
                                                >
                                                    Link
                                                </NButton>
                                            </div>
                                        ) : (
                                            <NButton
                                                className="w-full my-16"
                                                variant="outline"
                                                leftIcon="external"
                                                onClick={() =>
                                                    getAWSUrl(
                                                        environmentType,
                                                        selectedGroup,
                                                        selectedCurrentAccount,
                                                    )
                                                }
                                                isLoading={urlLoading}
                                                disabled={
                                                    selectedAccount === '' ||
                                                    selectedGroup === ''
                                                }
                                                square
                                            >
                                                Go to AWS console
                                            </NButton>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <p className="dialog-headline">
                                            Credentials
                                        </p>
                                        <CredentialsContainer
                                            title="Run the following commands in your terminal"
                                            exportCredentials={true}
                                            value={copyCredentials[0]}
                                            credentials={credentials}
                                        />
                                        <CredentialsContainer
                                            title="Copy the following commands in your AWS credentials file
        (typically located in ~/.aws/credentials)"
                                            exportCredentials={false}
                                            value={copyCredentials[1]}
                                            credentials={credentials}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </NDialog>
    );
}
